import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import React, { useEffect, useState } from 'react'
import { useMedia } from 'react-use'
import styled from 'styled-components'
import { EmptyCard } from '..'
import { usedChain } from '../../constants'
import { useCurrentCurrency } from '../../contexts/Application'
import { formattedNum, formatTime } from '../../utils'
import { updateNameData } from '../../utils/data'
import { ExplorerDataType, getExplorerLink } from '../../utils/etherscan'
import DropdownSelect from '../DropdownSelect'
import FormattedName from '../FormattedName'
import Link from '../Link'
import LocalLoader from '../LocalLoader'

dayjs.extend(utc)

const List = styled.div`
  width: 100%;
  background: transparent;

  > * {
    &:hover {
      background: rgba(179, 179, 190, 0.02);
    }
  }
`

const TableContainer = styled.div`
  width: 100%;
  background: transparent;
  border-radius: 8px;
  overflow: hidden;
`

const FilterButton = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  font-family: 'Inter Display', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => (props.active ? 'white' : 'rgba(255, 255, 255, 0.6)')};
  background: ${(props) => (props.active ? 'rgba(255, 255, 255, 0.08)' : 'transparent')};

  &:hover {
    color: white;
    background: ${(props) => (props.active ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.04)')};
  }

  transition: all 0.2s ease;
`

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  margin-bottom: 24px;
`

const HeaderGrid = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr 180px 180px 160px 140px;
  grid-gap: 24px;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  margin-bottom: 0;
  width: 100%;

  @media screen and (max-width: 1080px) {
    grid-template-columns: 240px 1fr 180px 180px 140px;
  }

  @media screen and (max-width: 780px) {
    grid-template-columns: 1fr 120px 100px;
    grid-gap: 16px;
  }

  > div {
    font-family: 'Inter Display', sans-serif;
    font-weight: 500;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.6);
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &:first-child {
      text-align: left;
    }

    &:not(:first-child) {
      text-align: right;
    }
  }
`

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 24px;
  width: 100%;

  .page-info {
    font-family: 'Inter Display', sans-serif;
    font-weight: 400;
    font-size: 14px;
    color: rgba(255, 255, 255, 0.6);
  }

  .nav-arrows {
    display: flex;
    gap: 4px;
  }

  .nav-arrow {
    cursor: pointer;
    width: 24px;
    height: 24px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;

    &:hover:not(.disabled) {
      background: rgba(255, 255, 255, 0.08);
    }

    &.disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    svg {
      width: 16px;
      height: 16px;
    }

    &:last-child svg {
    }
  }
`

const DashGrid = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr 180px 180px 160px 140px;
  grid-gap: 24px;
  align-items: center;
  padding: 16px 0;
  min-height: 56px;
  width: 100%;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);

  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }

  @media screen and (max-width: 1080px) {
    grid-template-columns: 240px 1fr 180px 180px 140px;
  }

  @media screen and (max-width: 780px) {
    grid-template-columns: 1fr 120px 100px;
    grid-gap: 16px;
  }
`

const DataText = styled.div`
  font-family: 'Inter Display', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: white;
  display: flex;
  align-items: center;

  &:first-child {
    text-align: left;
    justify-content: flex-start;
  }

  &:not(:first-child) {
    text-align: right;
    justify-content: flex-end;
  }

  @media screen and (max-width: 40em) {
    font-size: 12px;
  }

  a {
    color: white;
    text-decoration: none;

    &:hover {
      opacity: 0.8;
    }
  }
`

export const SortText = styled.button`
  cursor: pointer;
  font-weight: ${({ active }) => (active ? 500 : 400)};
  margin-right: 0.75rem !important;
  border: none;
  background-color: transparent;
  font-size: 1rem;
  padding: 0px;
  color: ${({ active, theme }) => (active ? theme.text1 : theme.text3)};
  outline: none;

  @media screen and (max-width: 600px) {
    font-size: 14px;
  }
`

const SORT_FIELD = {
  VALUE: 'amountUSD',
  AMOUNT0: 'token0Amount',
  AMOUNT1: 'token1Amount',
  TIMESTAMP: 'timestamp',
}

const TXN_TYPE = {
  ALL: 'All',
  SWAP: 'Swap',
  ADD: 'Add',
  REMOVE: 'Remove',
}

const ITEMS_PER_PAGE = 10

export function getTransactionType(event, symbol0, symbol1) {
  const formattedS0 = symbol0?.length > 8 ? symbol0.slice(0, 7) + '...' : symbol0
  const formattedS1 = symbol1?.length > 8 ? symbol1.slice(0, 7) + '...' : symbol1
  switch (event) {
    case TXN_TYPE.ADD:
      return 'Add ' + formattedS0 + ' and ' + formattedS1
    case TXN_TYPE.REMOVE:
      return 'Remove ' + formattedS0 + ' and ' + formattedS1
    case TXN_TYPE.SWAP:
      return 'Swap ' + formattedS0 + ' for ' + formattedS1
    default:
      return ''
  }
}

// @TODO rework into virtualized list
function TxnList({ transactions, color }) {
  // page state
  const [page, setPage] = useState(1)
  const [maxPage, setMaxPage] = useState(1)

  // sorting
  const [sortDirection] = useState(true)
  const [sortedColumn] = useState(SORT_FIELD.TIMESTAMP)
  const [filteredItems, setFilteredItems] = useState()
  const [txFilter, setTxFilter] = useState(TXN_TYPE.ALL)

  const [currency] = useCurrentCurrency()

  useEffect(() => {
    setMaxPage(1) // edit this to do modular
    setPage(1)
  }, [transactions])

  // parse the txns and format for UI
  useEffect(() => {
    if (transactions && transactions.mints && transactions.burns && transactions.swaps) {
      let newTxns = []
      if (transactions.mints.length > 0) {
        transactions.mints.map((mint) => {
          let newTxn = {}
          newTxn.hash = mint.transaction.id
          newTxn.timestamp = mint.transaction.timestamp
          newTxn.type = TXN_TYPE.ADD
          newTxn.token0Amount = mint.amount0
          newTxn.token1Amount = mint.amount1
          newTxn.account = mint.to
          newTxn.token0Symbol = updateNameData(mint.pair).token0.symbol
          newTxn.token1Symbol = updateNameData(mint.pair).token1.symbol
          newTxn.amountUSD = mint.amountUSD
          return newTxns.push(newTxn)
        })
      }
      if (transactions.burns.length > 0) {
        transactions.burns.map((burn) => {
          let newTxn = {}
          newTxn.hash = burn.transaction.id
          newTxn.timestamp = burn.transaction.timestamp
          newTxn.type = TXN_TYPE.REMOVE
          newTxn.token0Amount = burn.amount0
          newTxn.token1Amount = burn.amount1
          newTxn.account = burn.sender
          newTxn.token0Symbol = updateNameData(burn.pair).token0.symbol
          newTxn.token1Symbol = updateNameData(burn.pair).token1.symbol
          newTxn.amountUSD = burn.amountUSD
          return newTxns.push(newTxn)
        })
      }
      if (transactions.swaps.length > 0) {
        transactions.swaps.map((swap) => {
          const netToken0 = swap.amount0In - swap.amount0Out
          const netToken1 = swap.amount1In - swap.amount1Out

          let newTxn = {}

          if (netToken0 < 0) {
            newTxn.token0Symbol = updateNameData(swap.pair).token0.symbol
            newTxn.token1Symbol = updateNameData(swap.pair).token1.symbol
            newTxn.token0Amount = Math.abs(netToken0)
            newTxn.token1Amount = Math.abs(netToken1)
          } else if (netToken1 < 0) {
            newTxn.token0Symbol = updateNameData(swap.pair).token1.symbol
            newTxn.token1Symbol = updateNameData(swap.pair).token0.symbol
            newTxn.token0Amount = Math.abs(netToken1)
            newTxn.token1Amount = Math.abs(netToken0)
          }

          newTxn.hash = swap.transaction.id
          newTxn.timestamp = swap.transaction.timestamp
          newTxn.type = TXN_TYPE.SWAP

          newTxn.amountUSD = swap.amountUSD
          newTxn.account = swap.to
          return newTxns.push(newTxn)
        })
      }

      const filtered = newTxns.filter((item) => {
        if (txFilter !== TXN_TYPE.ALL) {
          return item.type === txFilter
        }
        return true
      })
      setFilteredItems(filtered)
      let extraPages = 1
      if (filtered.length % ITEMS_PER_PAGE === 0) {
        extraPages = 0
      }
      if (filtered.length === 0) {
        setMaxPage(1)
      } else {
        setMaxPage(Math.floor(filtered.length / ITEMS_PER_PAGE) + extraPages)
      }
    }
  }, [transactions, txFilter])

  useEffect(() => {
    setPage(1)
  }, [txFilter])

  const filteredList =
    filteredItems &&
    filteredItems
      .sort((a, b) => {
        return parseFloat(a[sortedColumn]) > parseFloat(b[sortedColumn])
          ? (sortDirection ? -1 : 1) * 1
          : (sortDirection ? -1 : 1) * -1
      })
      .slice(ITEMS_PER_PAGE * (page - 1), page * ITEMS_PER_PAGE)

  const below1080 = useMedia('(max-width: 1080px)')
  const below780 = useMedia('(max-width: 780px)')

  const ListItem = ({ item }) => {
    return (
      <DashGrid>
        <DataText>
          <Link color="white" external href={getExplorerLink(usedChain, item.hash, ExplorerDataType.TRANSACTION)}>
            {getTransactionType(item.type, item.token1Symbol, item.token0Symbol)}
          </Link>
        </DataText>
        <DataText>
          {currency === 'ETH' ? 'Ξ ' + formattedNum(item.valueETH) : formattedNum(item.amountUSD, true)}
        </DataText>
        {!below780 && (
          <DataText>
            {formattedNum(item.token1Amount)} <FormattedName text={item.token1Symbol} maxCharacters={5} margin={true} />
          </DataText>
        )}
        {!below780 && (
          <DataText>
            {formattedNum(item.token0Amount)} <FormattedName text={item.token0Symbol} maxCharacters={5} margin={true} />
          </DataText>
        )}
        {!below1080 && (
          <DataText>
            <Link color="white" external href={getExplorerLink(usedChain, item.account, ExplorerDataType.ADDRESS)}>
              {item.account && item.account.slice(0, 6) + '...' + item.account.slice(38, 42)}
            </Link>
          </DataText>
        )}
        <DataText>{formatTime(item.timestamp)}</DataText>
      </DashGrid>
    )
  }

  return (
    <TableContainer>
      {/* Filter Tabs */}
      <FilterContainer>
        {below780 ? (
          <DropdownSelect options={TXN_TYPE} active={txFilter} setActive={setTxFilter} color={color} />
        ) : (
          <>
            <FilterButton active={txFilter === TXN_TYPE.ALL} onClick={() => setTxFilter(TXN_TYPE.ALL)}>
              All
            </FilterButton>
            <FilterButton active={txFilter === TXN_TYPE.SWAP} onClick={() => setTxFilter(TXN_TYPE.SWAP)}>
              Swaps
            </FilterButton>
            <FilterButton active={txFilter === TXN_TYPE.ADD} onClick={() => setTxFilter(TXN_TYPE.ADD)}>
              Adds
            </FilterButton>
            <FilterButton active={txFilter === TXN_TYPE.REMOVE} onClick={() => setTxFilter(TXN_TYPE.REMOVE)}>
              Removes
            </FilterButton>
          </>
        )}
      </FilterContainer>

      {/* Table Header */}
      <HeaderGrid>
        <div>Activity</div>
        <div>Total value</div>
        {!below780 && <div>Token amount</div>}
        {!below780 && <div>Token amount</div>}
        {!below1080 && <div>Account</div>}
        <div>Time</div>
      </HeaderGrid>

      {/* Table Body */}
      <List p={0}>
        {!filteredList ? (
          <LocalLoader />
        ) : filteredList.length === 0 ? (
          <EmptyCard>No recent transactions found.</EmptyCard>
        ) : (
          filteredList.map((item, index) => {
            return <ListItem key={index} index={index + 1} item={item} />
          })
        )}
      </List>

      {/* Pagination */}
      <PaginationContainer>
        <div className="page-info">
          {Math.min((page - 1) * ITEMS_PER_PAGE + 1, filteredList?.length || 0)}-
          {Math.min(page * ITEMS_PER_PAGE, filteredList?.length || 0)} of {filteredList?.length || 0}
        </div>
        <div className="nav-arrows">
          <div className={`nav-arrow ${page === 1 ? 'disabled' : ''}`} onClick={() => page > 1 && setPage(page - 1)}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M10 4L6 8L10 12"
                stroke="rgba(255, 255, 255, 0.6)"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div
            className={`nav-arrow ${page >= maxPage ? 'disabled' : ''}`}
            onClick={() => page < maxPage && setPage(page + 1)}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M6 4L10 8L6 12"
                stroke="rgba(255, 255, 255, 0.6)"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      </PaginationContainer>
    </TableContainer>
  )
}

export default TxnList
